import {
  unref,
  provide,
  inject,
  onUnmounted,
  getCurrentInstance,
  ComponentInternalInstance,
  reactive,
  isReactive
} from 'vue';
import { PlainObject } from './types';

interface ItemCfg {
  owner?: ComponentInternalInstance;
  cleaner?: () => void;
}

// 对vue3 的onUnmount修正
// vue3, onUnmount返回的函数, 调用不能删除hooks
// https://github.com/vuejs/core/blob/c71a08e6fd44ee06c6b4f61d67727a7b7503605e/packages/runtime-core/src/apiLifecycle.ts
/*function onUnmountedHack(cb: Function, instance: ComponentInternalInstance | any) {
	let hooks = instance['um'] as Function[] ?? (instance['um'] = []);
	hooks.push(cb);

	return () => {
		const i = hooks.indexOf(cb);
		if(i >= 0) {
			hooks.splice(i, 1);
			cb();
		}
	}
}*/

function registerUnmount(
  target: PlainObject,
  itemCfgMap: Record<string, ItemCfg>,
  prop: string,
  owner: ComponentInternalInstance,
  changeNotify?: ItemChange
) {
  const cleaner = () => {
    const cfg = itemCfgMap[prop];
    if (!cfg || cfg.owner !== owner) {
      return;
    }

    Reflect.deleteProperty(target, prop);
    Reflect.deleteProperty(itemCfgMap, prop);

    changeNotify?.(prop, undefined);
  };

  const ok = onUnmounted(cleaner);
  if (!ok) throw new Error('onUnmounted fail');

  return cleaner;
}

function writer(
  target: PlainObject,
  itemCfgMap: Record<string, ItemCfg>,
  prop: string,
  owner: ComponentInternalInstance,
  changeNotify?: ItemChange
) {
  return (v: any) => {
    const cfg = itemCfgMap[prop];
    if (cfg) {
      if (cfg.owner == owner) {
        target[prop] = v;
        changeNotify?.(prop, v);
        return;
      }

      throw new Error(`prop ${prop} owner changed`);
    }

    throw new Error(`prop ${prop} not found`);
  };
}

function writerBuilder(
  target: PlainObject,
  itemCfgMap: Record<string, ItemCfg>,
  changeNotify?: ItemChange
) {
  return (prop: string) => {
    // 无当前组件
    const instance = getCurrentInstance();
    if (!instance) {
      console.warn(`access share data ${prop} fail: no current instance`);
      return undefined;
    }

    let cfg: ItemCfg;
    if (itemCfgMap[prop]) {
      cfg = itemCfgMap[prop];
      if (cfg.owner === instance) {
        return writer(target, itemCfgMap, prop, instance, changeNotify);
      }

      // console.warn("");

      // 执行清除
      cfg.cleaner?.();
    }

    cfg = {
      owner: instance,
      cleaner: registerUnmount(target, itemCfgMap, prop, instance, changeNotify)
    };
    itemCfgMap[prop] = cfg;

    return writer(target, itemCfgMap, prop, instance, changeNotify);
  };
}

type ItemChange = (name: string, value: any) => void;

/**
 * 创建一个带代理的对象, 会跟踪是哪个组件拥有修改权, 在unmount时会卸载key
 * 修改的对象的属性方法如下：
 *   let obj = protectedReactive();
 *   let propWriter = obj.$(prop);
 *   propWriter("write value"); // 修改属性值
 *   propWriter(undefined, true); // 释放权
 * @param o 初始化值, 初始化不会补跟踪
 * @returns
 */
export function protectedReactive<T extends PlainObject = PlainObject>(
  o?: PlainObject,
  changeNotify?: ItemChange | PlainObject
) {
  const obj = {} as PlainObject;

  // 转换结构
  if (o) {
    for (const [k, v] of Object.entries(o)) {
      obj[k] = { value: v };
    }
  }

  if (isReactive(changeNotify)) {
    const rv = changeNotify as PlainObject;
    changeNotify = (name: string, value: any) => {
      if (value === undefined) {
        delete rv[name];
      } else {
        rv[name] = value;
      }
    };
  }

  // 配置数据
  const itemCfgMap: Record<string, ItemCfg> = {};
  const reactiveObj = reactive(obj);

  return new Proxy(obj as T, {
    get(_, prop: string, receiver: any) {
      // 写函数
      if (prop == '$') {
        return writerBuilder(
          reactiveObj,
          itemCfgMap,
          changeNotify as ItemChange
        );
      }
      return unref(Reflect.get(reactiveObj, prop, receiver));
    },

    set(_, prop: string) {
      throw new Error(`please use $('${prop}')(value) to modify it`);
    },

    deleteProperty(_, prop: string) {
      const cfg = itemCfgMap[prop];
      if (cfg) {
        throw new Error(
          `please use $('${prop}')(undefined, true) to delete it`
        );
      }

      return Reflect.deleteProperty(reactiveObj, prop);
    },

    has(_, p) {
      return Reflect.has(reactiveObj, p);
    },

    ownKeys() {
      return Object.keys(reactiveObj);
    },

    getOwnPropertyDescriptor() {
      return {
        enumerable: true,
        configurable: true
      };
    }
  });
}

/**
 *
 * @param scope 范围, 在同一个页面是可能存在多个共享域, 比如search, form
 * @param configs 数据项
 */
export function shareData(scope: string, configs: PlainObject) {
  for (const [name, value] of Object.entries(configs)) {
    const key = scope + '~' + name;
    provide(key, value);
  }
}

export function useShareData(scope: string, item: string): any {
  const key = scope + '~' + item;
  return inject(key, undefined) as any;
}
